import { axiosInstance } from '../../utils/axiosApp';
import { Preferences } from '../../utils/types';
import qs from 'qs';
import { StateCreator } from 'zustand';
import {
  isPreferredGame,
  PREFERRED_GAMES_DEFAULT,
  PreferencesFields,
  SESSION_COOLDOWN_DURATION_DEFAULT_MINUTES,
  SESSION_LENGTH_DEFAULT_MINUTES,
  SESSION_LENGTH_MAX_MINUTES,
  SESSION_LENGTH_MIN_MINUTES,
} from '../../utils/constants';

const SESSION_LENGTH_DEFAULT = SESSION_LENGTH_DEFAULT_MINUTES;
const SESSION_COOLDOWN_DURATION_DEFAULT =
  SESSION_COOLDOWN_DURATION_DEFAULT_MINUTES;

const IMAGE_GENERATION_DEFAULT = false;
const VIDEO_GENERATION_DEFAULT = false;
let preferencesContextSequence = 0;

const normalizeSessionLength = (value: unknown): number => {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) {
    return SESSION_LENGTH_DEFAULT;
  }
  return Math.min(
    SESSION_LENGTH_MAX_MINUTES,
    Math.max(SESSION_LENGTH_MIN_MINUTES, parsed)
  );
};

const toPreferencesPayload = (preferences: Preferences) => ({
  session_length: normalizeSessionLength(preferences.sessionLength),
  session_cooldown_duration: preferences.cooldownPeriod,
  image_gen: preferences.imageGeneration,
  video_gen: preferences.videoGeneration,
  preferred_games: [...preferences.preferredGames],
  games_only_allowed: preferences.gamesOnlyAllowed,
});

export interface ProfilePreferencesState {
  preferences: Preferences;
  savedPreferences: Preferences | null;
  preferencesProfileId: string | null;
  preferencesErrorProfileId: string | null;

  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchPreferences: (profileId: string) => Promise<void>;
  resetProfilePreferences: () => void;
  discardPreferences: () => void;

  updatePreferences: (field: keyof Preferences, value: any) => void;

  savePreferences: (profileId: string) => Promise<boolean>;
}

export const createProfilePreferencesStateStore: StateCreator<
  ProfilePreferencesState
> = (set, get) => ({
  preferences: {
    sessionLength: undefined,
    cooldownPeriod: undefined,
    imageGeneration: IMAGE_GENERATION_DEFAULT,
    videoGeneration: VIDEO_GENERATION_DEFAULT,
    gamesOnlyAllowed: true,
    preferredGames: [...PREFERRED_GAMES_DEFAULT],
  },
  preferencesProfileId: null,
  savedPreferences: null,
  preferencesErrorProfileId: null,

  loading: true,
  saving: false,
  error: null,
  resetProfilePreferences: () => {
    preferencesContextSequence += 1;
    set({
      preferences: {
        sessionLength: undefined,
        cooldownPeriod: undefined,
        imageGeneration: IMAGE_GENERATION_DEFAULT,
        videoGeneration: VIDEO_GENERATION_DEFAULT,
        gamesOnlyAllowed: true,
        preferredGames: [...PREFERRED_GAMES_DEFAULT],
      },
      preferencesProfileId: null,
      savedPreferences: null,
      preferencesErrorProfileId: null,
      loading: false,
      saving: false,
      error: null,
    });
  },
  discardPreferences: () => {
    const { savedPreferences, saving } = get();
    if (savedPreferences && !saving) set({ preferences: savedPreferences });
  },

  fetchPreferences: async (profileId: string) => {
    if (!profileId) return;

    const contextSequence = preferencesContextSequence + 1;
    preferencesContextSequence = contextSequence;
    set((state) => ({
      loading: true,
      saving: false,
      error: null,
      preferencesErrorProfileId: null,
      preferencesProfileId:
        state.preferencesProfileId === profileId ? profileId : null,
    }));

    const name = Object.values(PreferencesFields);

    try {
      const res = await axiosInstance().get(
        `/api/profiles/${profileId}/preferences`,
        {
          timeout: 15_000,
          params: { name },
          paramsSerializer: (params: any) =>
            qs.stringify(params, { arrayFormat: 'repeat' }),
        }
      );

      if (res.status !== 200) throw new Error('Failed to fetch preferences');
      if (preferencesContextSequence !== contextSequence) {
        return;
      }

      const data = res.data;

      const nextPrefs: Preferences = {
        sessionLength:
          data.session_length !== undefined &&
          data.session_length !== null &&
          data.session_length !== ''
            ? normalizeSessionLength(data.session_length)
            : SESSION_LENGTH_DEFAULT,

        cooldownPeriod:
          data.session_cooldown_duration !== undefined &&
          data.session_cooldown_duration !== null &&
          data.session_cooldown_duration !== ''
            ? parseInt(data.session_cooldown_duration, 10)
            : SESSION_COOLDOWN_DURATION_DEFAULT,

        imageGeneration:
          data.image_gen !== undefined && data.image_gen !== null
            ? data.image_gen
            : IMAGE_GENERATION_DEFAULT,

        videoGeneration:
          data.video_gen !== undefined && data.video_gen !== null
            ? data.video_gen
            : VIDEO_GENERATION_DEFAULT,

        gamesOnlyAllowed: data.games_only_allowed,
        preferredGames: Array.isArray(data.preferred_games)
          ? data.preferred_games.filter(isPreferredGame)
          : [...PREFERRED_GAMES_DEFAULT],
      };

      set({
        preferences: nextPrefs,
        savedPreferences: nextPrefs,
        preferencesProfileId: profileId,
        preferencesErrorProfileId: null,
        loading: false,
      });
    } catch (e) {
      if (preferencesContextSequence !== contextSequence) {
        return;
      }
      if ((e as { profileAccessHandled?: boolean })?.profileAccessHandled) {
        set({ loading: false });
        return;
      }
      console.error('Error fetching preferences:', e);

      const fallback: Preferences = {
        sessionLength: SESSION_LENGTH_DEFAULT,
        cooldownPeriod: SESSION_COOLDOWN_DURATION_DEFAULT,
        imageGeneration: IMAGE_GENERATION_DEFAULT,
        videoGeneration: VIDEO_GENERATION_DEFAULT,
        gamesOnlyAllowed: true,
        preferredGames: [...PREFERRED_GAMES_DEFAULT],
      };

      set((state) => {
        const hasLoadedProfile = state.preferencesProfileId === profileId;
        return {
          ...(hasLoadedProfile
            ? {}
            : {
                preferences: fallback,
                savedPreferences: null,
                preferencesProfileId: null,
              }),
          error: 'An error occurred while fetching settings',
          preferencesErrorProfileId: profileId,
          loading: false,
        };
      });
    }
  },

  updatePreferences: (field, value) =>
    set((state) => ({
      preferences: {
        ...state.preferences,
        [field]: value,
      },
    })),

  savePreferences: async (profileId: string) => {
    const currentState = get();
    if (
      !profileId ||
      currentState.preferencesProfileId !== profileId ||
      currentState.savedPreferences === null ||
      currentState.preferencesErrorProfileId === profileId ||
      currentState.loading ||
      currentState.saving
    ) {
      return false;
    }

    const contextSequence = preferencesContextSequence;
    const preferences = currentState.preferences;
    const payload = toPreferencesPayload(preferences);
    set({ saving: true, error: null });

    try {
      const res = await axiosInstance().put(
        `/api/profiles/${profileId}/preferences`,
        payload,
        { timeout: 30_000 }
      );

      if (res.status !== 204) throw new Error('Failed to save preferences');
      if (
        preferencesContextSequence !== contextSequence ||
        get().preferencesProfileId !== profileId
      ) {
        return false;
      }

      const savedPreferences: Preferences = {
        ...preferences,
        sessionLength: normalizeSessionLength(preferences.sessionLength),
        preferredGames: [...preferences.preferredGames],
      };

      set({
        saving: false,
        savedPreferences,
        preferences:
          get().preferences === preferences
            ? savedPreferences
            : get().preferences,
      });

      return true;
    } catch (e) {
      if (
        preferencesContextSequence !== contextSequence ||
        get().preferencesProfileId !== profileId
      ) {
        return false;
      }
      if ((e as { profileAccessHandled?: boolean })?.profileAccessHandled) {
        set({ saving: false });
        return false;
      }
      console.error('Error saving preferences:', e);
      set({ saving: false, error: 'An error occurred while saving settings' });
      return false;
    }
  },
});
